import type { ReadonlyJSONValue } from "@rocicorp/zero";
import { styled } from "goober";
import { useMemo, useState } from "react";
import { queries } from "../../../../shared/queries";
import { useQuery } from "../../../hooks/useQuery";
import { formatTimestamp } from "../../../lib/formatTimestamp";
import { playClick } from "../../../lib/sounds";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Button } from "../../Button";
import { CodeBlock } from "../../CodeBlock";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { InfiniteScroll } from "../../InfiniteScroll";
import { Modal } from "../../Modal";
import { SpreadsheetTable, Td, Th, Thead } from "../../SpreadsheetTable";
import { Text } from "../../Text";
import { ScrollArea } from "../shared";

const PAGE_SIZE = 50;

const LinkCell = styled(Td)`
  cursor: pointer;
  position: relative;

  & > .link-icon {
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    padding-right: 16px;
    padding-left: 24px;
    align-items: center;
    background: linear-gradient(
      to right,
      transparent,
      var(--surface-hover) 40%
    );
  }

  &:hover > .link-icon {
    display: flex;
  }
`;

const ModalContent = styled("div")`
  width: 560px;
  height: 480px;
  padding: 12px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const columns = [
  { key: "timestamp", label: "Time", width: 180 },
  { key: "user", label: "User" },
  { key: "operation", label: "Operation" },
  { key: "source", label: "Source", width: 120 },
  { key: "status", label: "Status", width: 120 },
  { key: "error", label: "Error", width: 120 },
  { key: "inputs", label: "Input", width: 120 },
  { key: "output", label: "Output", width: 120 },
  { key: "context", label: "Request context", width: 120 },
] as const;

export function AuditLogsList() {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [auditLogs, auditLogsResult] = useQuery(queries.auditLogs({ limit }));
  const [allUsers] = useQuery(queries.allUsers());
  const [detailModal, setDetailModal] = useState<{
    title: string;
    data: ReadonlyJSONValue | string;
  } | null>(null);

  const usersById = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    for (const user of allUsers) {
      map.set(user.id, { name: user.name, email: user.email });
    }
    return map;
  }, [allUsers]);

  return (
    <ScrollArea>
      {auditLogs.length === 0 ? (
        auditLogsResult.type === "complete" ? (
          <EmptyState
            iconName="logs"
            title="No audit logs"
            description="Team actions appear here."
          />
        ) : null
      ) : (
        <SpreadsheetTable>
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.key}
                style={"width" in col ? { width: col.width } : undefined}
              />
            ))}
          </colgroup>
          <Thead>
            <tr>
              {columns.map((col) => (
                <Th key={col.key}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {col.label}
                  </Text>
                </Th>
              ))}
            </tr>
          </Thead>
          <tbody>
            {auditLogs.map((log) => {
              const user = log.userId ? usersById.get(log.userId) : undefined;
              return (
                <tr key={log.id}>
                  <Td>
                    <Text variant="text-small">
                      {log.timestamp ? formatTimestamp(log.timestamp) : ""}
                    </Text>
                  </Td>
                  <Td>
                    <Text variant="text-small" ellipsis>
                      {user ? userDisplayName(user) : log.userId}
                    </Text>
                  </Td>
                  <Td>
                    <Text variant="text-small" ellipsis>
                      {log.operationName}
                    </Text>
                  </Td>
                  <Td>
                    <Text variant="text-small" ellipsis>
                      {log.source}
                    </Text>
                  </Td>
                  <Td>
                    <Text
                      variant={log.success ? "text-small" : "text-small-md"}
                      color={log.success ? undefined : "var(--red-600)"}
                    >
                      {log.success ? "Success" : "Failed"}
                    </Text>
                  </Td>
                  {log.error ? (
                    <LinkCell
                      onMouseDown={() => {
                        playClick();
                        setDetailModal({
                          title: "Error",
                          data: log.error!,
                        });
                      }}
                    >
                      <Text variant="text-small" color="var(--red)">
                        {typeof log.error === "string"
                          ? "1 error"
                          : `${Object.keys(log.error as object).length} field${Object.keys(log.error as object).length === 1 ? "" : "s"}`}
                      </Text>
                      <span className="link-icon">
                        <Icon
                          name="maximize-2"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </span>
                    </LinkCell>
                  ) : (
                    <Td />
                  )}
                  <LinkCell
                    onMouseDown={() => {
                      playClick();
                      setDetailModal({
                        title: "Inputs",
                        data: log.inputs,
                      });
                    }}
                  >
                    <Text variant="text-small">
                      {typeof log.inputs === "object" && log.inputs !== null
                        ? `${Object.keys(log.inputs).length} field${Object.keys(log.inputs).length === 1 ? "" : "s"}`
                        : "1 field"}
                    </Text>
                    <span className="link-icon">
                      <Icon
                        name="maximize-2"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    </span>
                  </LinkCell>
                  {log.output ? (
                    <LinkCell
                      onMouseDown={() => {
                        playClick();
                        setDetailModal({
                          title: "Output",
                          data: log.output!,
                        });
                      }}
                    >
                      <Text variant="text-small">
                        {typeof log.output === "object"
                          ? `${Object.keys(log.output).length} field${Object.keys(log.output).length === 1 ? "" : "s"}`
                          : "1 field"}
                      </Text>
                      <span className="link-icon">
                        <Icon
                          name="maximize-2"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </span>
                    </LinkCell>
                  ) : (
                    <Td />
                  )}
                  {log.ipAddress || log.userAgent ? (
                    <LinkCell
                      onMouseDown={() => {
                        playClick();
                        const context: Record<string, string> = {};
                        if (log.ipAddress) context.ipAddress = log.ipAddress;
                        if (log.userAgent) context.userAgent = log.userAgent;
                        setDetailModal({
                          title: "Request context",
                          data: context,
                        });
                      }}
                    >
                      <Text variant="text-small">
                        {log.ipAddress && log.userAgent
                          ? "2 fields"
                          : "1 field"}
                      </Text>
                      <span className="link-icon">
                        <Icon
                          name="maximize-2"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </span>
                    </LinkCell>
                  ) : (
                    <Td />
                  )}
                </tr>
              );
            })}
          </tbody>
        </SpreadsheetTable>
      )}
      <InfiniteScroll
        hasMore={auditLogs.length >= limit}
        onLoadMore={() => setLimit((prev) => prev + PAGE_SIZE)}
      />
      <Modal open={detailModal !== null} onClose={() => setDetailModal(null)}>
        {detailModal && (
          <ModalContent>
            <ModalHeader>
              <Text variant="text-regular-md" ellipsis>
                {detailModal.title}
              </Text>
              <Button
                size="l"
                variant="secondary"
                icon="x"
                onClick={() => setDetailModal(null)}
              />
            </ModalHeader>
            <CodeBlock
              fill
              content={
                typeof detailModal.data === "string"
                  ? detailModal.data
                  : JSON.stringify(detailModal.data, null, 2)
              }
            />
          </ModalContent>
        )}
      </Modal>
    </ScrollArea>
  );
}
